import React, { useState, useEffect, useRef } from 'react';
import { Switch, Row, Col, Typography, message, Spin } from 'antd';
import axios from 'axios';

const { Text } = Typography;

const PreventScoreToggles = ({ patientId, initialValues, onToggleChange }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingInitial, setFetchingInitial] = useState(true);
  const [toggleStates, setToggleStates] = useState({
    prevent_smoker_status: false,
    prevent_diabetes_status: false,
    prevent_statin_use: false,
    prevent_bp_medication_use: false
  });

  // Fetch initial toggle states from the patient record
  useEffect(() => {
    const fetchInitialStates = async () => {
      if (!patientId) return;

      try {
        setFetchingInitial(true);

        const response = await axios.get(`/patients/${patientId}`);
        const patient = response.data.data.patient;
        const demographics = patient.demographics || {};

        const newStates = {
          prevent_smoker_status: demographics.prevent_smoker_status || false,
          prevent_diabetes_status: demographics.prevent_diabetes_status || false,
          prevent_statin_use: demographics.prevent_statin_use || false,
          prevent_bp_medication_use: demographics.prevent_bp_medication_use || false
        };
        setToggleStates(newStates);

        // Notify parent component of initial values
        if (onToggleChange) {
          const updatedValues = {
            smoker: newStates.prevent_smoker_status,
            diabetes: newStates.prevent_diabetes_status,
            onStatin: newStates.prevent_statin_use,
            onBPMeds: newStates.prevent_bp_medication_use
          };
          onToggleChange(updatedValues);
        }
      } catch (error) {
        console.error('Error fetching initial PREVENT Score states:', error);
        // Fall back to initialValues if provided
        if (initialValues) {
          const newStates = {
            prevent_smoker_status: initialValues.smoker || false,
            prevent_diabetes_status: initialValues.diabetes || false,
            prevent_statin_use: initialValues.onStatin || false,
            prevent_bp_medication_use: initialValues.onBPMeds || false
          };
          setToggleStates(newStates);
        }
      } finally {
        setFetchingInitial(false);
      }
    };

    fetchInitialStates();
  }, [patientId]); // Only depend on patientId, not initialValues

  const handleToggleChange = async (parameterKey, checked) => {
    setLoading(true);

    try {
      // Update local state immediately for responsive UI
      const newToggleStates = {
        ...toggleStates,
        [parameterKey]: checked
      };
      setToggleStates(newToggleStates);

      // Save to backend using standardized parameter naming
      const payload = {
        demographics: {
          [parameterKey]: checked
        }
      };

      await axios.patch(`/patients/${patientId}`, payload);

      // Notify parent component of the change for real-time calculation updates
      if (onToggleChange) {
        const updatedValues = {
          smoker: newToggleStates.prevent_smoker_status,
          diabetes: newToggleStates.prevent_diabetes_status,
          onStatin: newToggleStates.prevent_statin_use,
          onBPMeds: newToggleStates.prevent_bp_medication_use
        };
        onToggleChange(updatedValues);
      }

      message.success('PREVENT Score parameter updated successfully');
    } catch (error) {
      console.error('Error updating PREVENT Score parameter:', error);

      // Revert local state on error
      setToggleStates(prevState => ({
        ...prevState,
        [parameterKey]: !checked
      }));

      message.error('Failed to update PREVENT Score parameter');
    } finally {
      setLoading(false);
    }
  };

  const toggleConfig = [
    {
      key: 'prevent_smoker_status',
      label: 'Smoker',
      description: 'Current smoking status'
    },
    {
      key: 'prevent_diabetes_status', 
      label: 'Diabetes',
      description: 'Has diabetes diagnosis'
    },
    {
      key: 'prevent_statin_use',
      label: 'On Statin',
      description: 'Currently taking statin medication'
    },
    {
      key: 'prevent_bp_medication_use',
      label: 'On BP Meds',
      description: 'Currently taking blood pressure medication'
    }
  ];

  if (fetchingInitial) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin size="small" />
        <div style={{ marginTop: 8, color: '#666' }}>Loading PREVENT Score parameters...</div>
      </div>
    );
  }

  return (
    <Spin spinning={loading} data-testid="loading-spinner">
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: '14px', color: '#595959', marginBottom: 12, display: 'block' }}>
          Risk Factor Parameters
        </Text>
        <Row gutter={[16, 12]}>
          {toggleConfig.map(config => (
            <Col xs={24} sm={12} key={config.key}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '8px 12px',
                background: '#fafafa',
                borderRadius: '6px',
                border: '1px solid #f0f0f0'
              }}>
                <div>
                  <Text strong style={{ fontSize: '13px' }}>{config.label}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {config.description}
                  </Text>
                </div>
                <Switch
                  checked={toggleStates[config.key]}
                  onChange={(checked) => handleToggleChange(config.key, checked)}
                  size="small"
                  disabled={loading}
                />
              </div>
            </Col>
          ))}
        </Row>
      </div>
    </Spin>
  );
};

export default PreventScoreToggles;
